/**
 * Sends browser-side failures to the server log so they are visible without
 * a user report. Best effort: never throws, never blocks, sends at most a
 * handful of reports per page load.
 */
const maxReportsPerLoad = 5;
let sent = 0;
let installed = false;

interface ClientErrorContext {
  area: string;
  componentStack?: string | null;
}

export function reportClientError(
  error: unknown,
  context: ClientErrorContext,
): void {
  if (sent >= maxReportsPerLoad) return;
  sent += 1;
  const detail = describe(error);
  const body = JSON.stringify({
    area: context.area,
    name: detail.name,
    message: detail.message.slice(0, 500),
    stack: (detail.stack ?? "").slice(0, 2000),
    componentStack: (context.componentStack ?? "").slice(0, 1000),
    url: window.location.pathname,
    userAgent: navigator.userAgent.slice(0, 200),
  });
  try {
    if (!navigator.sendBeacon?.("/api/v1/client-errors", body)) {
      void fetch("/api/v1/client-errors", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => undefined);
    }
  } catch {
    /* reporting must never break the app */
  }
}

/** Installs the global handlers once; safe to call from main.tsx. */
export function installClientErrorReporting(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (event) => {
    reportClientError(event.error ?? event.message, { area: "window" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportClientError(event.reason, { area: "promise" });
  });
}

function describe(error: unknown): {
  name: string;
  message: string;
  stack: string | null;
} {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ?? null,
    };
  }
  return { name: "NonError", message: String(error), stack: null };
}
